import { Component, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import Swal from 'sweetalert2';
import { MatDialog } from '@angular/material/dialog';
import { DeliveryDialogComponent } from './delivery-dialog/delivery-dialog.component';
import { DeliveryService } from './delivery.service';

@Component({
  selector: 'app-delivery',
  templateUrl: './delivery.component.html',
  styleUrls: ['./delivery.component.scss']
})
export class DeliveryComponent implements OnInit {

  displayedColumns: string[] = [
    'customerName',
    'contactNumber',
    'driverName',
    'vehicleNumber',
    'pickupLocation',
    'item',
    'deliveryLocation',
    'receiverContact',
    'notes',
    'amount',
    'isPaid',
    'status',
    'paymentReference',
    'orderDate',
    'actions'
  ];

  dataSource = new MatTableDataSource<any>([]);
  totalOrders = 0;
  loading = false;

  filter = {
    search: '',
    from: '',
    to: '',
    isPaid: ''
  };

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(private deliveryService: DeliveryService, private dialog: MatDialog) {}

  ngOnInit(): void {
    this.loadTodayOrders();
  }

  /** ✅ Load today’s orders */
  loadTodayOrders(): void {
    this.loading = true;
    this.deliveryService.getToday().subscribe({
      next: (data) => {
        console.log('[Today Orders]', data);

        this.dataSource = new MatTableDataSource(data || []);
        this.totalOrders = data?.length || 0;

        if (this.paginator) this.dataSource.paginator = this.paginator;
        if (this.sort) this.dataSource.sort = this.sort;

        this.loading = false;
      },
      error: (err) => {
        console.error('[Today Orders Error]', err);
        this.loading = false;
        Swal.fire('Error', 'Failed to load today’s orders.', 'error');
      }
    });
  }
  /** ✅ Cancel a delivery order */
  cancelOrder(order: any): void {
    Swal.fire({
      title: 'Cancel Delivery',
      input: 'textarea',
      inputPlaceholder: 'Enter cancellation remark...',
      inputAttributes: { 'aria-label': 'Enter cancellation remark' },
      showCancelButton: true,
      confirmButtonText: 'Confirm Cancel',
      confirmButtonColor: '#d33',
      preConfirm: (remark) => {
        if (!remark || remark.trim() === '') {
          Swal.showValidationMessage('A remark is required to cancel an order.');
          return false;
        }
        return remark.trim();
      }
    }).then((result) => {
      if (result.isConfirmed) {
        const remark = result.value;
        this.deliveryService.cancelOrder(order.id, { remark }).subscribe({
          next: () => {
            Swal.fire('Cancelled', 'Order has been cancelled successfully.', 'success');
  
            // ✅ Update the row immediately (no need to reload all)
            order.isCancelled = true;
            order.cancelRemark = remark;
  
            // ✅ Force Angular to detect change
            this.dataSource.data = [...this.dataSource.data];
          },
          error: (err) => {
            console.error('[Cancel Error]', err);
            Swal.fire('Error', 'Failed to cancel order.', 'error');
          }
        });
      }
    });
  }
  
  /** ✅ Apply filters */
  applyFilters(): void {
    this.loading = true;
    const { from, to, isPaid, search } = this.filter;
  
    this.deliveryService.getFiltered({ from, to, isPaid, search }).subscribe({
      next: (res) => {
        console.log('[Filtered Result]', res);
        
  
        // FIX: use res.items instead of res.data
        this.dataSource = new MatTableDataSource(res.items || []);
        this.totalOrders = res.total || 0;
  
        if (this.paginator) {
          this.dataSource.paginator = this.paginator;
          this.paginator.firstPage();
        }
        if (this.sort) this.dataSource.sort = this.sort;
  
        this.loading = false;
      },
      error: (err) => {
        console.error('[Filter Error]', err);
        this.loading = false;
        Swal.fire('Error', 'Failed to filter orders.', 'error');
      }
    });
  }
  

  /** ✅ Mark as paid */
  markAsPaid(order: any): void {
    Swal.fire({
      title: 'Mark as Paid?',
      text: `Confirm marking payment for ${order.customerName}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, confirm',
    }).then((result) => {
      if (result.isConfirmed) {
        this.deliveryService
          .markAsPaid(order.id, {
            isPaid: true,
            paymentReference: `REF-${Math.floor(Math.random() * 1_000_000)}`
          })
          .subscribe({
            next: () => {
              Swal.fire('Success', 'Order marked as paid.', 'success');
              this.loadTodayOrders();
            },
            error: (err) => {
              console.error('[Mark as Paid Error]', err);
              Swal.fire('Error', 'Failed to mark as paid.', 'error');
            }
          });
      }
    });
  }

  /** ✅ Reset filters */
  resetFilters(): void {
    this.filter = { search: '', from: '', to: '', isPaid: '' };
    this.loadTodayOrders();
  }

  /** ✅ Search bar */
  applySearch(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.filter.search = filterValue.trim();
    this.applyFilters();
  }

  /** ✅ Open Create Dialog */
  openCreateDialog(): void {
    const dialogRef = this.dialog.open(DeliveryDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      disableClose: true,
      panelClass: 'app-modal-panel',
      backdropClass: 'app-modal-backdrop'
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) this.loadTodayOrders();
    });
  }
}
